/* eslint-disable no-nested-ternary */
import { Box, Typography } from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import { useMemo } from 'react';
import { WalletLog } from '../../../apiTypes';
import { chartColors1, chartColors2 } from '../../../constants/charts';
import { Currency } from '../../../constants/currencies';
import TransactionCategories from '../../../constants/transactionCategories';
import { t } from '../../../constants/translations';
import { useAdmin } from '../../../contexts/admin.context';
import { Lang, useLocale } from '../../../contexts/locale.context';
import currency from '../../../utils/currency';
import useMedia from '../../../utils/useMedia';

type Props = {
  logs: WalletLog[];
};

export const logCategoryMap: Record<WalletLog['type'], keyof typeof t> = {
  transfer: TransactionCategories.transfer,
  supply: TransactionCategories.supply,
  shift: TransactionCategories.salary,
  order: TransactionCategories.in_house_sales,
  custom: 'unknown',
  cancel: 'unknown',
  cancelOrder: 'unknown',
};

export const getChartData = (logs: WalletLog[], lang: Lang) => {
  const incomeMap: Record<string, number> = {};
  const spendMap: Record<string, number> = {};

  logs.forEach((log) => {
    const category = log.category
      ? t[log.category as keyof typeof t]?.[lang] || log.category
      : t[logCategoryMap[log.type] || 'unknown'][lang];

    if (log.sum > 0) {
      incomeMap[category] = (Number(incomeMap[category]) || 0) + log.sum;
    } else {
      spendMap[category] = (Number(spendMap[category]) || 0) - log.sum;
    }

    if (log.bankCommission) {
      spendMap[t.bank_commission[lang]] = (Number(spendMap[t.bank_commission[lang]]) || 0) + log.bankCommission;
    }
  });
  return [incomeMap, spendMap];
};

export const Legends = ({
  keys,
  chartData = {},
  cafeCurrency,
  colors,
}: {
  keys: string[];
  chartData?: Record<string, number>;
  cafeCurrency?: Currency;
  colors: string[];
}) => (
  <Box sx={{ display: 'flex', mb: 4, flexWrap: 'wrap', px: 2 }}>
    {keys.map((key, index) => (
      <Box key={key} sx={{ display: 'flex', gap: 1, alignItems: 'center', mr: 2 }}>
        <Box sx={{ width: 12, height: 12, bgcolor: colors[index % 10], borderRadius: '2px' }} />
        <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
          {key}: {currency(chartData[key], cafeCurrency, true)}
        </Typography>
      </Box>
    ))}
  </Box>
);

type BarProps = {
  title: string;
  total: number;
  max: number;
  width: number;
  cafeCurrency?: Currency;
  data: Record<string, number | string>[];
  keys: string[];
  colors: string[];
};

const Bar = ({ total, title, width, cafeCurrency, data, keys, max, colors }: BarProps) => (
  <>
    <Typography variant="h6" sx={{ px: 2 }}>
      {title}: {currency(total, cafeCurrency)}
    </Typography>
    <Box sx={{ width, height: 50 }}>
      <ResponsiveBar
        data={data}
        keys={keys}
        indexBy="name"
        layout="horizontal"
        borderRadius={4}
        innerPadding={2}
        valueFormat={(v) => `${currency(v, cafeCurrency, true)} (${Math.round((Number(v) / total) * 100)}%)`}
        label={(v) => `${Math.round((Number(v.value) / total) * 100)}%`}
        labelSkipWidth={30}
        labelTextColor={{ from: 'color', modifiers: [['darker', 1.5]] }}
        axisLeft={null}
        axisBottom={null}
        maxValue={max}
        enableGridY={false}
        colors={colors}
      />
    </Box>
  </>
);

const WalletsPie = ({ logs }: Props) => {
  const { lang } = useLocale();
  const { cafe } = useAdmin();
  const { isMobile } = useMedia();

  const chartData = useMemo(() => getChartData(logs, lang), [logs, lang]);

  const totalIncome = useMemo(() => Object.values(chartData[0] || {}).reduce((acc, v) => acc + v, 0), [chartData]);
  const totalSpend = useMemo(() => Object.values(chartData[1] || {}).reduce((acc, v) => acc + v, 0), [chartData]);

  const data1 = useMemo(() => [{ ...chartData[0]!, name: t.income[lang] }], [chartData, lang]);
  const data2 = useMemo(() => [{ ...chartData[1]!, name: t.expenses[lang] }], [chartData, lang]);

  const keys1 = useMemo(() => Object.keys(chartData[0] || {}), [chartData]);
  const keys2 = useMemo(() => Object.keys(chartData[1] || {}), [chartData]);

  const hasChart = !!totalSpend || !!totalIncome;
  const width = Math.min(isMobile ? window.innerWidth : window.innerWidth - 360, 1000);

  if (!hasChart) {
    return null;
  }

  return (
    <Box sx={{ mb: 6 }}>
      <Bar
        title={t.income[lang]}
        total={totalIncome}
        max={Math.max(totalIncome, totalSpend)}
        width={width}
        cafeCurrency={cafe?.currency}
        data={data1}
        keys={keys1}
        colors={chartColors1}
      />
      <Legends keys={keys1} chartData={chartData[0]} cafeCurrency={cafe?.currency} colors={chartColors1} />

      <Bar
        title={t.expenses[lang]}
        total={totalSpend}
        max={Math.max(totalIncome, totalSpend)}
        width={width}
        cafeCurrency={cafe?.currency}
        data={data2}
        keys={keys2}
        colors={chartColors2}
      />

      <Legends keys={keys2} chartData={chartData[1]} cafeCurrency={cafe?.currency} colors={chartColors2} />
    </Box>
  );
};

export default WalletsPie;
